import _ from 'lodash'
import Vue from 'vue'
import i18n from '@/i18n'
import {generateKey} from '../../mixins/keyGenerator'

export default {
  namespaced: true,

  state: {
    name: null,
    dateTimeZone: null,
    dateTimeCulture: null,
    isInteractive: false,
    isInverted: false,
    floors: {},
    current: null,
    imageDimensions: {},
    defaultSettings: {},
    idlenessKey: null,
    viewerEntityId: 0
  },

  getters: {
    getFloorWorkspaces: (state) => (floorId, floorPlanId) => {
      const floor = _.get(state.floors, floorId, null)
      const floorPlans = _.get(floor, 'floorPlans', [])
      return _.chain(floorPlans)
        .filter(x => floorPlanId ? x.id === floorPlanId : true)
        .map(x => {
          return _.get(x, 'workspaces', [])
        })
        .flatten()
        .uniq()
        .value()
    },
    orderedFloorsByLevel: state => {
      return _.orderBy(state.floors, 'level', 'desc')
    },
    getOrderedFloorPlansByName: state => floorId => {
      const floor = _.get(state.floors, floorId, null)
      const floorPlans = _.get(floor, 'floorPlans', [])
      return getOrderedFloorPlansByName(floorPlans)
    }
  },

  mutations: {
    setData(state, data) {
      _.set(state, 'name', _.get(data, 'name', null))
      _.set(state, 'dateTimeZone', _.get(data, 'dateTimeZone', false))
      _.set(state, 'dateTimeCulture', _.get(data, 'dateTimeCulture', false))
      _.set(state, 'isInteractive', _.get(data, 'isInteractive', false))
      _.set(state, 'isInverted', _.get(data, 'isInverted', false))
      _.set(state, 'viewerEntityId', _.get(data, 'viewerEntityId', 0))
      i18n.locale = _.get(data, 'language', 'en')

      const floors = _.get(data, 'floors', [])
      _.forEach(floors, f => {
        const floorId = _.get(f, 'id', null)
        Vue.set(state.floors, floorId, f)
      })

      const defaultFloorId = _.get(data, 'defaultFloorId', false)
      const defaultFloorPlanId = _.get(data, 'defaultFloorPlanId', false)
      const defaultFloor = _.find(floors, f => f.id === defaultFloorId)
      const defaultFloorPlans = _.get(defaultFloor, 'floorPlans', [])
      const defaultFloorPlan = _.find(defaultFloorPlans, p => p.id === defaultFloorPlanId)

      if (defaultFloorPlan) {
        const defaultSettings = {
          floorId: defaultFloorId,
          floorPlanId: defaultFloorPlanId,
          jsonData: _.get(defaultFloorPlan, 'jsonData', null)
        }
        _.set(state, 'defaultSettings', defaultSettings)
        _.set(state, 'current', defaultSettings)
        return
      }

      const floor = _.chain(floors)
        .orderBy(['level'], ['desc'])
        .first()
        .value()

      const floorPlans = _.get(floor, 'floorPlans', [])
      const floorPlan = _.first(floorPlans) || null
      const defaultSettings = {
        floorId: _.get(floor, 'id', null),
        floorPlanId: _.get(floorPlan, 'id', null),
        jsonData: _.get(floorPlan, 'jsonData', null)
      }
      _.set(state, 'defaultSettings', defaultSettings)
      _.set(state, 'current', defaultSettings)
    },
    updateBuildingData(state, data) {
      _.set(state, 'dateTimeZone', _.get(data, 'dateTimeZone', null))
      _.set(state, 'dateTimeCulture', _.get(data, 'dateTimeCulture', null))

      i18n.locale = _.get(data, 'language', 'en')
    },
    setCurrent(state, {floorId, floorPlanId}) {
      const floor = _.get(state.floors, floorId, null)
      if (!floor) throw Error(`Could not find floor ${floorId}.`)

      const floorPlans = _.get(floor, 'floorPlans', [])
      if (floorPlans.length === 0) throw Error('At least 1 floorplan is required.')

      const floorPlan = floorPlanId
        ? _.find(floorPlans, f => f.id === floorPlanId)
        : _.first(getOrderedFloorPlansByName(floorPlans))

      _.set(state, 'current', {
        floorId: _.get(floor, 'id', null),
        floorPlanId: _.get(floorPlan, 'id', null),
        jsonData: _.get(floorPlan, 'jsonData', null)
      })
    },
    setImageDimensions(state, {width, height}) {
      if (width < 1 || height < 1) {
        throw new Error('The given dimensions was invalid.')
      }
      state.imageDimensions = {width: width, height: height}
    },
    reset(state) {
      _.set(state, 'current', state.defaultSettings)
      _.set(state, 'idlenessKey', generateKey())
    }
  },

  actions: {
    load({commit}, data) {
      commit('setData', data)
    },
    updateBuildingData({commit}, data) {
      commit('updateBuildingData', data)
    },
    selectFloorPlan({commit, state}, {floorId, floorPlanId}) {
      const currentFloorId = _.get(state.current, 'floorId', null)
      if (!floorPlanId && currentFloorId === floorId) {
        return
      }
      const currentFloorPlanId = _.get(state.current, 'floorPlanId', null)
      if (currentFloorId === floorId && currentFloorPlanId === floorPlanId) {
        return
      }
      commit('setCurrent', {floorId, floorPlanId})
    },
    setImageDimensions({commit}, dimensions) {
      commit('setImageDimensions', dimensions)
    },
    reset({commit}) {
      commit('reset')
    }
  }
}

function getOrderedFloorPlansByName(floorPlans) {
  return _.chain(floorPlans)
    .map(x => {
      return {
        item: x,
        normalizedName: _.get(x, 'name', '').toUpperCase()
      }
    })
    .orderBy('normalizedName', 'asc')
    .map(x => x.item)
    .value()
}



// WEBPACK FOOTER //
// ./src/store/modules/livePlan.js